import { Stack } from '@fluentui/react';
import { memo, useCallback, useEffect, useRef } from 'react';

import type { User } from '../../../authz/gen/ai/h2o/user/v1/user_pb';
import { useUsers } from '../../../authz/providers/UsersProvider';
import { MembersDropdown } from '../../../pages/ManageWorkspaces/components/ManageMembers/MembersDropdown';
import type { PageFilterProps } from '../types';

type UserFilterProps = PageFilterProps<string> & {
  label?: string;
  readableValuePrefix?: string;
};

export const UserFilter = memo((props: UserFilterProps) => {
  const { onChange, value, label = 'Filter by user', readableValuePrefix = 'User' } = props;
  const { usersNameMap = {} } = useUsers();
  const prepopulatedUser = value ? usersNameMap[value] : undefined;
  const hasInitialized = useRef(false);
  const previousUsersNameMapLength = useRef(0);

  useEffect(() => {
    const currentLength = Object.keys(usersNameMap).length;
    if (currentLength !== previousUsersNameMapLength.current) {
      hasInitialized.current = false;
      previousUsersNameMapLength.current = currentLength;
    }
  }, [usersNameMap]);

  useEffect(() => {
    if (value && usersNameMap && Object.keys(usersNameMap).length > 0 && !hasInitialized.current) {
      const user = usersNameMap[value];
      if (user) {
        const displayName = user?.displayName || user?.name || value;
        const readableValue = `${readableValuePrefix}: ${displayName}`;
        onChange(value, readableValue);
        hasInitialized.current = true;
      }
    }
  }, [value, usersNameMap, onChange, readableValuePrefix]);

  const handleUserChange = useCallback(
    (option: User | undefined) => {
      const userName = option?.name;
      const displayName = option?.displayName || option?.name || '';
      const readableValue = displayName ? `${readableValuePrefix}: ${displayName}` : '';

      if (userName) onChange(userName, readableValue);
    },
    [onChange, readableValuePrefix]
  );

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
      <MembersDropdown
        label={label}
        prepopulatedUser={prepopulatedUser}
        onUserChange={(option) => handleUserChange(option as User)}
      />
    </Stack>
  );
});
